/**
 * The key-value budget (Atlas ch.3).
 *
 * Send-all-bids emits roughly five key-values per bidder. At nine bidders that
 * is ~50, and they all ride in the `scp` parameter of a URL. Past the request's
 * length limit the tail is simply truncated — which kills whichever bids were
 * unlucky enough to be serialised last, with no error anywhere. A bid that
 * reaches GAM with no targeting cannot match a line item and cannot win.
 *
 * So we shed deliberately rather than letting the wire shed arbitrarily, in a
 * fixed order, so what is lost is always the least valuable.
 */

/** Conservative ceiling on the serialised `scp` payload. GPT's own limit is not
 *  published as a hard number and varies with the rest of the URL, so this is
 *  set well below where truncation is observed rather than at it — the failure
 *  is silent, which makes the safe side the only side worth being on. */
export const DEFAULT_KEY_VALUE_BUDGET_CHARS = 7500;

/** `hb_pb`, `hb_adid`, … with no bidder suffix. Prebid emits these for the
 *  winning bid, duplicating that bidder's own suffixed set. */
export function isUnsuffixed(key, bidders) {
  return !bidders.some((b) => key.endsWith(`_${b}`));
}

/** Length of the serialised form GPT will send, so the budget is measured
 *  against what actually travels rather than against the object. */
export function serializedLength(targeting) {
  let n = 0;
  for (const [k, v] of Object.entries(targeting)) {
    n += k.length + String(v).length + 2; // key=value plus a separator
  }
  return n;
}

/**
 * Fit targeting into the budget.
 *
 * @param {Record<string,string>} targeting  Flat key → value, as Prebid emits.
 * @param {object} opts
 * @param {string[]} opts.bidders            Bidder codes present, for suffix matching.
 * @param {Record<string, number>} [opts.cpmByBidder] Used to shed the cheapest first.
 * @param {number} [opts.budgetChars]
 * @returns {{targeting: Record<string,string>, shedBidders: string[], droppedUnsuffixed: boolean, chars: number}}
 */
export function fitToBudget(targeting, { bidders = [], cpmByBidder = {}, budgetChars = DEFAULT_KEY_VALUE_BUDGET_CHARS } = {}) {
  let kept = { ...targeting };
  const shedBidders = [];
  let droppedUnsuffixed = false;

  if (serializedLength(kept) <= budgetChars) {
    return { targeting: kept, shedBidders, droppedUnsuffixed, chars: serializedLength(kept) };
  }

  // 1 — the un-suffixed set. It duplicates the winning bidder's own suffixed
  // set, which is already present, so nothing is lost but redundancy.
  const withoutUnsuffixed = {};
  for (const [k, v] of Object.entries(kept)) {
    if (isUnsuffixed(k, bidders)) {
      droppedUnsuffixed = true;
      continue;
    }
    withoutUnsuffixed[k] = v;
  }
  if (droppedUnsuffixed) kept = withoutUnsuffixed;
  if (serializedLength(kept) <= budgetChars) {
    return { targeting: kept, shedBidders, droppedUnsuffixed, chars: serializedLength(kept) };
  }

  // 2 — whole bidder sets, lowest bid first. A bidder that cannot win is a
  // bidder whose keys cost nothing to omit. Bidders with no known price go
  // first: an unpriced key set is the least defensible thing on the request.
  const order = [...bidders].sort((a, b) => (cpmByBidder[a] ?? -1) - (cpmByBidder[b] ?? -1));
  for (const bidder of order) {
    if (serializedLength(kept) <= budgetChars) break;
    const next = {};
    let removed = false;
    for (const [k, v] of Object.entries(kept)) {
      // 3 — never partially. A bidder with hb_pb but no hb_adid matches a line
      // item and then renders nothing: a blank slot, which is strictly worse
      // than not competing at all.
      if (k.endsWith(`_${bidder}`)) {
        removed = true;
        continue;
      }
      next[k] = v;
    }
    if (removed) {
      kept = next;
      shedBidders.push(bidder);
    }
  }

  return { targeting: kept, shedBidders, droppedUnsuffixed, chars: serializedLength(kept) };
}
